import React, { useEffect, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, useSignUp } from '@clerk/react';
import LogRocket from 'logrocket';
import { analytics } from '@client/analytics';
import {
  clerkMessage,
  formatByCountry,
  stripDigits,
  validateForCountry,
  type SupportedCountryCode,
} from '@client/util/clerkAuthHelpers';

export { SUPPORTED_COUNTRIES, type SupportedCountryCode } from '@client/util/clerkAuthHelpers';
import { analytics as posthogAnalytics } from '@shared/posthog';
import { isEdwardJonesEmail } from '@shared';
import { authApi } from '@client/api/waterlily';

export type PilotBlockedReason = 'edj_advisor_invite_required';

const emailFieldSchema = z
  .string()
  .trim()
  .min(1, 'Email is required')
  .toLowerCase()
  .email('Please enter a valid email address');

export const clerkSignupSchema = z
  .object({
    email: emailFieldSchema,
    countryCode: z.enum(['+1', '+91', '+63']),
    phoneNumber: z.string().min(1, 'Phone number is required'),
  })
  .superRefine((val, ctx) => {
    const err = validateForCountry(stripDigits(val.phoneNumber), val.countryCode);
    if (err) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['phoneNumber'], message: err });
  });

export const clerkSignupSchemaEmailOnly = z.object({
  email: emailFieldSchema,
  countryCode: z.enum(['+1', '+91', '+63']),
  phoneNumber: z.string().optional(),
});

export type ClerkSignupFormValues = z.infer<typeof clerkSignupSchema>;

type Step = 'start' | 'verify_phone' | 'check_email';

export { clerkMessage } from '@client/util/clerkAuthHelpers';

// Dedicated verify page for sign-up — read-only display of
// signUp.verifications.emailLinkVerification.status. It never calls
// finalize/setActive, so the link tab won't activate the session; the
// originating tab is the only one that calls signUp.finalize().
const MAGIC_LINK_REDIRECT = `${typeof window === 'undefined' ? '' : window.location.origin}/sign-up/verify`;

type ClerkLikeError = { longMessage?: string; message?: string } | null;

export function useClerkSignupFlow() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const redirectTo = searchParams.get('redirect_to') || '/';
  const emailHint = searchParams.get('email');

  const { signUp, errors: clerkErrors, fetchStatus } = useSignUp();
  const { isSignedIn } = useAuth();

  const isEmailOnlyMode = searchParams.get('override_connection') === 'email';

  const [step, setStep] = useState<Step>('start');
  const [phoneCode, setPhoneCode] = useState('');
  const [emailLinkVerified, setEmailLinkVerified] = useState(false);
  const [phoneCodeResentAt, setPhoneCodeResentAt] = useState<number | null>(null);
  const [emailLinkResentAt, setEmailLinkResentAt] = useState<number | null>(null);

  const [isStartSubmitting, setIsStartSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResendingPhone, setIsResendingPhone] = useState(false);
  const [isResendingLink, setIsResendingLink] = useState(false);
  const [submitError, setSubmitError] = useState<ClerkLikeError>(null);
  const [pilotBlockedReason, setPilotBlockedReason] = useState<PilotBlockedReason | null>(null);

  const RESEND_COOLDOWN_MS = 15000;

  useEffect(() => {
    if (!phoneCodeResentAt) return;
    const timer = setTimeout(() => setPhoneCodeResentAt(null), RESEND_COOLDOWN_MS);
    return () => clearTimeout(timer);
  }, [phoneCodeResentAt]);

  useEffect(() => {
    if (!emailLinkResentAt) return;
    const timer = setTimeout(() => setEmailLinkResentAt(null), RESEND_COOLDOWN_MS);
    return () => clearTimeout(timer);
  }, [emailLinkResentAt]);

  const resolver: Resolver<ClerkSignupFormValues> = isEmailOnlyMode
    ? (zodResolver(clerkSignupSchemaEmailOnly) as unknown as Resolver<ClerkSignupFormValues>)
    : zodResolver(clerkSignupSchema);
  const form = useForm<ClerkSignupFormValues>({
    resolver,
    mode: 'onChange',
    defaultValues: { email: emailHint ?? '', countryCode: '+1', phoneNumber: '' },
  });

  // Doc-style sequential flow per Clerk's custom email-link sign-up guide.
  // No useMutation — replaces the previous mutation/effect split to rule out
  // React Query as a contributor to the "you're signed out" flash. Verbose
  // logging captures the actual Clerk error shape at each branch so we can
  // identify the failure mode from real data rather than guess.
  async function awaitVerificationAndFinalize() {
    const startedAt = Date.now();
    const { error: waitErr } = await signUp.verifications.waitForEmailLinkVerification();
    if (waitErr) {
      const errorContext = {
        raw: JSON.stringify(waitErr, null, 2),
        elapsedMs: Date.now() - startedAt,
        signUpStatus: signUp.status,
        signUpId: signUp.id ?? '',
        emailLinkStatus: signUp.verifications.emailLinkVerification?.status ?? '',
        isSignedIn: String(isSignedIn),
      };
      console.error('[ClerkSignup] waitForEmailLinkVerification error:', errorContext);
      try {
        LogRocket.captureMessage('[ClerkSignup] Clerk waitForEmailLinkVerification error', {
          tags: { feature: 'auth', flow: 'email-link-signup' },
          extra: errorContext,
        });
      } catch {
        // LogRocket may not be initialized; ignore.
      }
      // HACK: Clerk SDK bug — waitForEmailLinkVerification returns 401
      // "signed_out" on consecutive polls. Reload to recover; if the user has
      // clicked the link, post-reload they'll be signed in via cookies.
      const innerCode = (waitErr as { errors?: { code?: string }[] }).errors?.[0]?.code;
      if (innerCode === 'signed_out') {
        window.location.reload();
        return;
      }
      setSubmitError(waitErr as ClerkLikeError);
      return;
    }

    setEmailLinkVerified(true);

    if (signUp.status !== 'complete') {
      console.warn('[ClerkSignup] sign-up not complete after verification', {
        status: signUp.status,
        isSignedIn,
      });
      return;
    }

    const { error: finalizeError } = await signUp.finalize({
      navigate: ({ session, decorateUrl }) => {
        if (session?.currentTask) return;
        const url = decorateUrl(redirectTo);
        if (url.startsWith('http')) window.location.href = url;
        else navigate(url, { replace: true });
      },
    });
    if (finalizeError) {
      console.error('[ClerkSignup] finalize error:', JSON.stringify(finalizeError, null, 2));
      setSubmitError(finalizeError as ClerkLikeError);
      return;
    }

    posthogAnalytics.auth.advisorSignedUp();
  }

  async function handleStartSubmit(values: ClerkSignupFormValues) {
    setSubmitError(null);
    setPilotBlockedReason(null);
    setIsStartSubmitting(true);

    // Gate Edward Jones advisor sign-ups to invited pilot members before any Clerk account
    // is created. Only EDJ emails hit the endpoint; the server backstop is authoritative, so
    // this is purely for UX. On a transient failure we surface a retryable error rather than
    // wrongly telling a genuine pilot advisor they aren't invited.
    if (isEdwardJonesEmail(values.email)) {
      try {
        const { eligible, reason } = await authApi.postAdvisorSignupEligibilityForPublic({
          email: values.email,
        });

        if (!eligible) {
          setPilotBlockedReason(reason ?? 'edj_advisor_invite_required');
          setIsStartSubmitting(false);
          return;
        }
      } catch {
        setSubmitError({ message: 'Unable to verify sign-up eligibility. Please try again.' });
        setIsStartSubmitting(false);
        return;
      }
    }

    if (isEmailOnlyMode) {
      analytics.formSubmit('signup_form', { email: values.email });

      const { error: createErr } = await signUp.create({
        emailAddress: values.email,
        legalAccepted: true,
      });
      if (createErr) {
        console.error(
          '[ClerkSignup] create error (email-only):',
          JSON.stringify(createErr, null, 2),
        );
        setSubmitError(createErr as ClerkLikeError);
        setIsStartSubmitting(false);
        return;
      }

      const { error: linkErr } = await signUp.verifications.sendEmailLink({
        verificationUrl: MAGIC_LINK_REDIRECT,
      });
      if (linkErr) {
        console.error('[ClerkSignup] sendEmailLink error:', JSON.stringify(linkErr, null, 2));
        setSubmitError(linkErr as ClerkLikeError);
        setIsStartSubmitting(false);
        return;
      }

      setStep('check_email');
      setIsStartSubmitting(false);
      await awaitVerificationAndFinalize();
      return;
    }

    const e164Phone = `${values.countryCode}${stripDigits(values.phoneNumber)}`;
    analytics.formSubmit('signup_form', { email: values.email, phoneNumber: e164Phone });

    const { error: createErr } = await signUp.create({
      emailAddress: values.email,
      phoneNumber: e164Phone,
      legalAccepted: true,
    });
    if (createErr) {
      console.error('[ClerkSignup] create error (phone):', JSON.stringify(createErr, null, 2));
      setSubmitError(createErr as ClerkLikeError);
      setIsStartSubmitting(false);
      return;
    }

    const { error: smsErr } = await signUp.verifications.sendPhoneCode();
    if (smsErr) {
      console.error('[ClerkSignup] sendPhoneCode error:', JSON.stringify(smsErr, null, 2));
      setSubmitError(smsErr as ClerkLikeError);
      setIsStartSubmitting(false);
      return;
    }

    setStep('verify_phone');
    setIsStartSubmitting(false);
  }

  async function handleVerifyPhoneSubmit(code: string) {
    setSubmitError(null);
    setIsVerifying(true);

    const { error: verifyErr } = await signUp.verifications.verifyPhoneCode({ code });
    if (verifyErr) {
      console.error('[ClerkSignup] verifyPhoneCode error:', JSON.stringify(verifyErr, null, 2));
      setSubmitError(verifyErr as ClerkLikeError);
      setIsVerifying(false);
      return;
    }

    const { error: linkErr } = await signUp.verifications.sendEmailLink({
      verificationUrl: MAGIC_LINK_REDIRECT,
    });
    if (linkErr) {
      console.error(
        '[ClerkSignup] sendEmailLink (post-phone) error:',
        JSON.stringify(linkErr, null, 2),
      );
      setSubmitError(linkErr as ClerkLikeError);
      setIsVerifying(false);
      return;
    }

    setStep('check_email');
    setIsVerifying(false);
    await awaitVerificationAndFinalize();
  }

  async function handleResendPhone() {
    setSubmitError(null);
    setIsResendingPhone(true);

    const { error } = await signUp.verifications.sendPhoneCode();
    if (error) {
      console.error('[ClerkSignup] resendPhone error:', JSON.stringify(error, null, 2));
      setSubmitError(error as ClerkLikeError);
      setIsResendingPhone(false);
      return;
    }

    setPhoneCode('');
    setPhoneCodeResentAt(Date.now());
    setIsResendingPhone(false);
  }

  async function handleResendLink() {
    setSubmitError(null);
    setIsResendingLink(true);

    const { error } = await signUp.verifications.sendEmailLink({
      verificationUrl: MAGIC_LINK_REDIRECT,
    });
    if (error) {
      console.error('[ClerkSignup] resendLink error:', JSON.stringify(error, null, 2));
      setSubmitError(error as ClerkLikeError);
      setIsResendingLink(false);
      return;
    }

    setEmailLinkResentAt(Date.now());
    setIsResendingLink(false);
  }

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = stripDigits(e.target.value);
    form.setValue('phoneNumber', formatByCountry(digits, form.getValues('countryCode')), {
      shouldValidate: true,
    });
  };

  const handleCountryCodeChange = (code: SupportedCountryCode) => {
    form.setValue('countryCode', code, { shouldValidate: true });
    const digits = stripDigits(form.getValues('phoneNumber'));
    form.setValue('phoneNumber', formatByCountry(digits, code), { shouldValidate: true });
  };

  const goBack = async () => {
    setPhoneCode('');
    setEmailLinkVerified(false);
    setSubmitError(null);
    setPilotBlockedReason(null);
    setIsStartSubmitting(false);
    setIsVerifying(false);
    await signUp.reset();
    setStep('start');
  };

  // If the user lands on /signup already signed in (e.g. clicked an old signup
  // link with a live Clerk session), there is no in-progress signUp attempt to
  // finalize — without this redirect the page hangs on "Signing you in…".
  useEffect(() => {
    if (isSignedIn !== true) return;
    if (signUp.id && signUp.status !== 'abandoned') return;
    navigate(redirectTo || '/', { replace: true });
  }, [isSignedIn, signUp.id, signUp.status, redirectTo, navigate]);

  const isSigningIn = isSignedIn === true;

  // Suppress Clerk's 401 "signed_out" bug from waitForEmailLinkVerification —
  // the reload effect above handles recovery once the link is actually clicked.
  const isClerkSignedOutBug =
    submitError != null &&
    Array.isArray((submitError as { errors?: unknown[] }).errors) &&
    (submitError as { errors: { code?: string }[] }).errors[0]?.code === 'signed_out';
  const mutationError = isClerkSignedOutBug ? null : clerkMessage(submitError);

  return {
    form,
    step,
    isSigningIn,
    emailLinkVerified,
    isEmailOnlyMode,

    onStartSubmit: form.handleSubmit(v => handleStartSubmit(v)),
    onVerifyPhoneSubmit: (e: React.FormEvent) => {
      e.preventDefault();
      if (phoneCode.length === 6) void handleVerifyPhoneSubmit(phoneCode);
    },

    handlePhoneNumberChange,
    handleCountryCodeChange,
    goBack,

    phoneCode,
    setPhoneCode,

    isStartSubmitting: isStartSubmitting || fetchStatus === 'fetching',
    isVerifying,

    emailAddress: signUp.emailAddress,
    phoneNumber: signUp.phoneNumber,

    mutationError,
    clerkErrors,

    pilotBlockedReason,
    dismissPilotBlock: () => {
      setPilotBlockedReason(null);
      form.setValue('email', '', { shouldValidate: true });
    },

    resendLink: handleResendLink,
    resendPhone: handleResendPhone,

    isResendingPhone,
    phoneCodeResent: phoneCodeResentAt !== null,
    isResendingLink,
    emailLinkResent: emailLinkResentAt !== null,
  };
}
